import {
  type CalculationInfoResponse,
  ContractLoanTypeEnum,
  FixedInterestPeriodType,
} from '@kk/api/modules/customerPortal';
import { formatCurrency } from '@kk/shared/utils/numbers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { match } from 'ts-pattern';

export type InfoRow = {
  id: string;
  label: string;
  value: string | undefined;
  tooltip?: string;
};

export type UseLoanInfoRowsOptions = {
  omitNotRelevantRows?: boolean;
};

export function useLoanInfoRows(
  calculation: CalculationInfoResponse,
  options: UseLoanInfoRowsOptions = { omitNotRelevantRows: false },
) {
  const { t } = useTranslation('ui');

  return useMemo(() => {
    const rows: InfoRow[] = [
      {
        label: t('loan.loanProduct'),
        id: 'loanProduct',
        value: calculation.loanProduct ?? t('unavailable'),
      },
      {
        id: 'principalAmount',
        label:
          calculation.loanProduct === ContractLoanTypeEnum.Byggekredit
            ? t('loan.principalAmount.credit')
            : t('loan.principalAmount'),
        value: formatCurrency(calculation.principalAmount),
      },
      {
        id: 'amortizationProfile',
        label: t('loan.amortizationProfile'),
        value: calculation.amortizationProfile
          ? t(`amortizationProfile.${calculation.amortizationProfile}`)
          : t('unavailable'),
      },
      {
        id: 'timeToMaturity',
        label: t('loanPeriod'),
        value: `${calculation.timeToMaturity?.years} ${t('year', { count: calculation.timeToMaturity?.years ?? 0 })}`,
      },
      {
        id: 'interestPaymentFrequency',
        label: t('rateFrequency'),
        value: match(calculation.loanProduct)
          .with(ContractLoanTypeEnum.Byggekredit, () => t('notRelevant'))
          .otherwise(() =>
            calculation.interestPaymentFrequency
              ? t(
                  `loan.interestPaymentFrequency.${calculation.interestPaymentFrequency}`,
                )
              : t('unavailable'),
          ),
      },
      {
        id: 'fixedInterestPeriodType',
        label:
          calculation.loanProduct === ContractLoanTypeEnum.KKcibor
            ? t('fixedInterestPeriod.cibor')
            : t('fixedInterestPeriod'),
        value: match(calculation.loanProduct)
          .with(
            ContractLoanTypeEnum.KKvar,
            ContractLoanTypeEnum.Byggekredit,
            () => t('notRelevant'),
          )
          .otherwise(() =>
            calculation.fixedInterestPeriodType == null ||
            calculation.fixedInterestPeriodType ===
              FixedInterestPeriodType.UnDefined
              ? t('unavailable')
              : match(calculation.fixedInterestPeriodType)
                  .with(FixedInterestPeriodType.ToMaturity, () =>
                    t('fixedInterestPeriod.ToMaturity'),
                  )
                  .with(
                    FixedInterestPeriodType.Years,
                    () =>
                      `${calculation.fixedInterestYears} ${t('year', { count: calculation.fixedInterestYears ?? 0 })}`,
                  )
                  .with(FixedInterestPeriodType.Cheapest, () => {
                    if (
                      calculation.fixedInterestYears ===
                      calculation.timeToMaturity?.years
                    ) {
                      return t('fixedInterestPeriod.ToMaturity');
                    }
                    return `${calculation.fixedInterestYears} ${t('year', { count: calculation.fixedInterestYears ?? 0 })}`;
                  })
                  .exhaustive(),
          ),
      },
    ];

    if (options.omitNotRelevantRows) {
      return rows.filter((row) => row.value !== t('notRelevant'));
    }

    return rows;
  }, [calculation, t, options]);
}

export default useLoanInfoRows;
